import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  logout,
  selectAdmin,
  selectSession,
} from "../../store/features/adminSlice";
import SportsCricketIcon from "@mui/icons-material/SportsCricket";
import AppShellStyle from "./appShell.module.scss";
// One place holds where the public site lives, so switching between localhost
// and the deployed domain is a single edit rather than a hunt through pages.
import { publicDomain } from "../../store/network routes/paths";

// Icons are drawn inline rather than imported from the SVG asset folder. The
// old sidebar recoloured its imported SVGs by injecting a <style> block per
// item; these inherit `currentColor` instead, so the active and hover states
// are plain CSS.
const icon = {
  home: (
    <path d="M3 10.5 12 3l9 7.5M5 9.5V20h5v-6h4v6h5V9.5" />
  ),
  teams: <path d="M12 3 5 6v5.5c0 4 2.9 7.7 7 9.5 4.1-1.8 7-5.5 7-9.5V6z" />,
  curators: (
    <>
      <rect x="4" y="4.5" width="16" height="16" rx="3" />
      <path d="M9 4.5h6v2.5H9z" />
      <path d="m8.5 13 2 2 4.5-4.5" />
    </>
  ),
  players: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19c0-3 2.5-4.8 5.5-4.8s5.5 1.8 5.5 4.8" />
      <path d="M16 5.6a3.2 3.2 0 0 1 0 6.1" />
      <path d="M17.5 14.6c2 .6 3.2 2.1 3.2 4.4" />
    </>
  ),
};

const NAV = [
  { text: "Home", link: "/", key: "home" },
  { text: "Team Owners", link: "/bidders", key: "owners" },
  { text: "Teams", link: "/teams", key: "teams" },
  { text: "Curators", link: "/curators", key: "curators" },
  { text: "Players", link: "/biddingPlayers", key: "players" },
];

function RailIcon({ name }) {
  // Owners is the one Material glyph in the rail. It is filled where the rest
  // are stroked, so it is set a little smaller to carry the same weight, and
  // it inherits `currentColor` like the drawn icons do.
  if (name === "owners") {
    return <SportsCricketIcon sx={{ fontSize: 19 }} />;
  }

  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {icon[name]}
    </svg>
  );
}

export default function AppShell({ children, bare }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const profile = useSelector(selectAdmin);
  const session = useSelector(selectSession);

  const name = profile?.name ? profile.name.split(" ")[0] : "Admin";
  const initial = name.charAt(0).toUpperCase();

  // "/" only matches itself; everything else matches its sub-routes too, so
  // /biddingPlayers/new keeps Players lit.
  const isActive = (link) =>
    link === "/"
      ? location.pathname === "/"
      : location.pathname.toLowerCase().startsWith(link.toLowerCase());

  return (
    <div className={AppShellStyle.shell}>
      <nav className={AppShellStyle.rail}>
        <Link to="/" className={AppShellStyle.mark} title="Auction">
          A
        </Link>

        {NAV.map((item) => (
          <Link
            key={item.link}
            to={item.link}
            className={`${AppShellStyle.railItem} ${
              isActive(item.link) ? AppShellStyle.active : ""
            }`}
          >
            <RailIcon name={item.key} />
            <span className={AppShellStyle.railTip}>{item.text}</span>
          </Link>
        ))}

        <div className={AppShellStyle.railSpacer} />

        <div className={AppShellStyle.railAvatar}>
          {profile?.image ? <img src={profile.image} alt="" /> : initial}
        </div>
      </nav>

      <div className={AppShellStyle.stack}>
        <header className={AppShellStyle.topbar}>
          <Link to="/" className={AppShellStyle.wordmark}>
            Auction
          </Link>

          <div className={AppShellStyle.topRight}>
            {/* Only shown while an auction session is running, as in the old
                top bar. It deep-links to that session's live page. */}
            {session?._id && (
              <a
                className={AppShellStyle.liveBtn}
                href={publicDomain + "/live/" + session?.sessionId}
                target="_blank"
                rel="noreferrer"
              >
                <span className={AppShellStyle.liveDot} />
                Auction Page
              </a>
            )}

            <a
              className={AppShellStyle.portalBtn}
              href={publicDomain + "/curator"}
              target="_blank"
              rel="noreferrer"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="16" rx="3" />
                <path d="M8 9h8M8 13h5" />
              </svg>
              Curator Portal
            </a>

            <div className={AppShellStyle.topDivider} />

            <div className={AppShellStyle.who}>
              <div className={AppShellStyle.whoText}>
                <div className={AppShellStyle.whoName}>{name}</div>
                <button
                  className={AppShellStyle.logout}
                  onClick={() => {
                    dispatch(logout());
                    window.location.reload();
                  }}
                >
                  Log out
                </button>
              </div>
              <div className={AppShellStyle.whoAvatar}>
                {profile?.image ? <img src={profile.image} alt="" /> : initial}
              </div>
            </div>
          </div>
        </header>

        {/* Keyed on the route so a navigation remounts this subtree and the
            enter animation replays. The rail and top bar are outside it and
            stay put, so only the part that actually changed moves.

            `bare` lets a page opt out of the white panel and lay out its own
            surfaces, which the dashboard needs for its card grid. */}
        <div key={location.pathname} className="au-page">
          {bare ? (
            children
          ) : (
            <main className={AppShellStyle.panel}>{children}</main>
          )}
        </div>
      </div>
    </div>
  );
}
